<template>
    <BaseDialog v-model:open="isOpen" :title="t('chat.goalPush.dialogTitle')">
        <div v-if="draftError" class="flex flex-col items-center gap-3 py-6 text-center" data-testid="goal-draft-error">
            <p class="body-small text-notification-error-text" role="alert">{{ draftError }}</p>
            <BaseButton theme="primary" @click="isOpen = false">{{ t("common.close") }}</BaseButton>
        </div>
        <div v-else-if="!draft" class="flex flex-col items-center gap-3 py-10 text-center" data-testid="goal-draft-loading">
            <LoadingSpinner class="h-6" />
            <p class="nadia-chat text-ink-muted">{{ t("chat.goalPush.drafting") }}</p>
        </div>
        <div v-else class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <p class="body-small text-ink-muted">{{ subtitle }}</p>
            </div>

            <template v-if="!submitted">
                <section v-for="(goal, index) in localGoals" :key="index" class="flex flex-col gap-2 pl-3 border-l-2 border-border">
                    <div class="flex items-center justify-between">
                        <h4 class="body-semibold text-ink">{{ t("chat.goalPush.goalHeading", { number: index + 1 }) }}</h4>
                        <BaseButton theme="tertiary" :disabled="loading" :aria-label="t('chat.goalPush.removeGoalLabel', { number: index + 1 })" @click="removeGoal(index)">
                            {{ t("chat.goalPush.remove") }}
                        </BaseButton>
                    </div>
                    <BaseTextarea
                        v-model:input="goal.name"
                        rows="1"
                        :disabled="loading"
                        :aria-label="t('chat.goalPush.goalTitle')"
                        :placeholder="t('chat.goalPush.goalTitle')"
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    <BaseTextarea
                        v-model:input="goal.description"
                        rows="2"
                        :disabled="loading"
                        :aria-label="t('chat.goalPush.goalDescription')"
                        :placeholder="t('chat.goalPush.goalDescriptionPlaceholder')"
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    <FormField :id="`due-${index}`" :label="t('chat.goalPush.dueDate')">
                        <BaseInput v-model="goal.due_date" type="date" :disabled="loading" />
                    </FormField>
                    <FormField v-if="categoryOptions.length" :id="`cat-${index}`" :label="t('chat.goalPush.goalType')">
                        <BaseSelect v-model="goal.goal_category_id" :options="categorySelectOptions" :disabled="loading" :placeholder="t('chat.goalPush.goalTypePlaceholder')" />
                    </FormField>
                    <label v-if="orgGoalOptions.length" :for="`org-goal-${index}`" class="goal-field">
                        <span class="goal-field-label">{{ t("chat.goalPush.orgGoal") }}</span>
                        <BaseCombobox
                            v-model="goal.organizationGoalOption"
                            :options="orgGoalOptions"
                            :input-id="`org-goal-${index}`"
                            :disabled="loading"
                            :placeholder="t('chat.goalPush.orgGoalPlaceholder')" />
                    </label>
                </section>

                <div>
                    <BaseButton theme="tertiary" :disabled="loading" data-testid="add-goal" @click="addGoal">{{ t("chat.goalPush.addGoal") }}</BaseButton>
                </div>

                <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="goal-push-submitted">
                <i class="bi bi-check-circle text-2xl text-chip-status-done-text" aria-hidden="true"></i>
                <p class="nadia-chat text-ink">{{ submittedMessage }}</p>
                <BaseButton theme="primary" @click="isOpen = false">{{ t("chat.goalPush.done") }}</BaseButton>
            </div>
        </div>
        <template v-if="draft && !draftError && !submitted" #footer>
            <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">{{ t("common.cancel") }}</BaseButton>
            <BaseButton
                theme="primary"
                data-testid="submit-goal-push-dialog"
                :disabled="loading || !hasContent"
                class="flex items-center justify-center gap-1"
                @click="handleSubmit">
                <LoadingSpinner v-if="loading" class="h-4" />{{ editingExisting ? t("chat.goalPush.update") : t("chat.goalPush.push") }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import FormField from "~vue/components/form/FormField.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { resolveActionRefusal } from "~vue/utils/actionRefusal";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface DraftGoal {
    goal_id: string;
    name: string;
    description: string;
    due_date: string;
    goal_category_id: string;
    organization_goal_id: string;
}
interface GoalPushDraft {
    goals: DraftGoal[];
}
interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    draft: GoalPushDraft | null;
    messageId: number;
    // The draft was reloaded from goals already in Workday (editing existing), not a fresh draft.
    editingExisting?: boolean;
    // Per-tenant options for the category and org-goal alignment selectors; empty hides the control.
    categoryOptions?: SelectOption[];
    orgGoalOptions?: SelectOption[];
    // Set when the background draft fetch failed; shown in place of the goal list.
    draftError?: string;
}

const props = withDefaults(defineProps<Props>(), {
    editingExisting: false,
    categoryOptions: () => [],
    orgGoalOptions: () => [],
    draftError: "",
});
const isOpen = defineModel<boolean>("open", { default: false });
const { t } = useI18n();

// BaseSelect keys its options; the API calls the same thing value.
const categorySelectOptions = computed(() => props.categoryOptions.map((option) => ({ key: option.value, label: option.label })));

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
const submittedMessage = ref("");
const noticeMessage = ref("");

const subtitle = computed(() => (props.editingExisting ? t("chat.goalPush.subtitleExisting") : t("chat.goalPush.subtitleDraft")));

// Client-side row: the org-goal alignment is held as the selected combobox option (resolved back
// to organization_goal_id on submit), the rest mirrors the server DraftGoal shape.
interface LocalGoal {
    goal_id: string;
    name: string;
    description: string;
    due_date: string;
    goal_category_id: string;
    organizationGoalOption: SelectOption | null;
}

function toLocalGoals(source: GoalPushDraft | null): LocalGoal[] {
    if (!source) {
        return [];
    }
    return source.goals.map((goal) => ({
        goal_id: goal.goal_id,
        name: goal.name,
        description: goal.description,
        due_date: goal.due_date,
        goal_category_id: goal.goal_category_id,
        organizationGoalOption: props.orgGoalOptions.find((option) => option.value === goal.organization_goal_id) ?? null,
    }));
}

const localGoals = reactive<LocalGoal[]>(toLocalGoals(props.draft));

const hasContent = computed(() => localGoals.some((goal) => goal.name.trim().length > 0));

// Reset the editable copy whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            localGoals.splice(0, localGoals.length, ...toLocalGoals(draft));
            submitted.value = false;
            submittedMessage.value = "";
            noticeMessage.value = "";
        }
    },
    { immediate: true },
);

function addGoal() {
    localGoals.push({ goal_id: "", name: "", description: "", due_date: "", goal_category_id: "", organizationGoalOption: null });
}

function removeGoal(index: number) {
    localGoals.splice(index, 1);
}

function pushSummary(created: number, updated: number): string {
    if (created > 0 && updated > 0) {
        return t("chat.goalPush.summaryCreatedAndUpdated", { created, updated });
    }
    if (created > 0) {
        return t("chat.goalPush.summaryCreated", { count: created }, created);
    }
    if (updated > 0) {
        return t("chat.goalPush.summaryUpdated", { count: updated }, updated);
    }
    return t("chat.goalPush.summaryDefault");
}

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_goals_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const goals = localGoals
            .map((goal) => ({
                goal_id: goal.goal_id,
                name: goal.name.trim(),
                description: goal.description.trim(),
                due_date: goal.due_date.trim(),
                goal_category_id: goal.goal_category_id,
                organization_goal_id: goal.organizationGoalOption?.value ?? "",
            }))
            .filter((goal) => goal.name.length > 0);
        const result = await resolveActionRefusal(execute(ChatAction.SUBMIT_GOAL_PUSH_DRAFT, { message_id: props.messageId, draft: { goals } }, { sync: true }));
        if (result?.success) {
            submittedMessage.value = pushSummary(result.payload?.created_count ?? goals.length, result.payload?.updated_count ?? 0);
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? t("chat.goalPush.pushError");
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = t("chat.goalPush.pushError");
    } finally {
        stopLoading();
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .goal-field {
        @apply flex flex-col gap-1;
    }

    .goal-field-label {
        @apply body-small text-control-label;
    }
}
</style>
